<template>
  <section
    class="coupon-teaser"
    :class="{ 'coupon-teaser--mini': miniVersion }"
  >
    <div class="coupon-teaser__copy">
      <Heading
        v-if="!miniVersion"
        text-uppercase
        font-weight="ultra"
        class="coupon-teaser__heading"
        :tag="headingTag"
      >
        {{ teaserHeading }}
      </Heading>
      <Paragraph class="coupon-teaser__paragraph">{{ teaserCopy }}</Paragraph>
      <div>
        <form
          v-if="!activeCoupon"
          class="coupon-teaser__form"
          @submit.prevent="submitCoupon"
        >
          <TextInput
            v-model="couponInput"
            id-prefix="coupon-teaser"
            placeholder="Gutscheincode/Coupon"
            required
            class="coupon-teaser__form-text"
          />
          <LinkButton
            class="coupon-teaser__form-submit coupon-teaser__form-submit--mobile"
            tag="button"
            primary
            type="submit"
          >
            Einlösen
          </LinkButton>
          <LinkButton
            class="coupon-teaser__form-submit coupon-teaser__form-submit--desktop"
            tag="button"
            primary
            type="submit"
          >
            Jetzt einlösen
          </LinkButton>
        </form>
        <div
          v-else
          class="coupon-teaser__form-post-submit"
        >
          <TextInput
            :placeholder="activeCoupon"
            disabled
            label="Dein Code:"
            class="coupon-teaser__form-active-coupon"
          />
          <LinkButton
            tag="button"
            aria-label="Code löschen"
            class="coupon-teaser__form-trash-button"
            transparent
            @click.stop="eraseCoupon"
          >
            <SvgIcon
              :image="trashCan"
              class="coupon-teaser__form-trash-icon"
            />
          </LinkButton>
        </div>
      </div>
      <Paragraph
        v-if="errorStatus"
        class="coupon-teaser__warning-message"
        >{{ content.warningMessages[errorStatus] }}</Paragraph
      >
    </div>
    <SvgIcon
      :image="heroImage"
      class="coupon-teaser__hero-image"
      no-icon-class
    />
  </section>
</template>

<script setup lang="ts">
import { defineComponent, ref } from 'vue';

import { Heading, Paragraph, LinkButton, TextInput } from '@i22-td-smarthome/component-library';
import SvgIcon from '@/components/svg-icon.vue';

import heroImage from '@/assets/images/rabatt-aktion-logo.svg';
import trashCan from '@/assets/images/delete.svg';

defineComponent({ name: 'CouponTeaser' });

const props = defineProps({
  activeCoupon: {
    type: String,
    default: '',
  },
  errorStatus: {
    type: String,
    default: '',
  },
  teaserHeading: {
    type: String,
    default: '',
  },
  teaserCopy: {
    type: String,
    default: '',
  },
  headingTag: {
    type: String,
    default: 'h1',
  },
  miniVersion: {
    type: Boolean,
  },
  customBrandError: {
    type: String,
    default: '',
  },
});

const content = {
  warningMessages: {
    invalid: 'Leider ist der eingegebene Code nicht gültig. Prüfe Deine Eingabe.',
    expired: 'Leider ist der eingegebene Code nicht mehr gültig.',
    redeemed: 'Der eingegebene Coupon/Gutschein wurde bereits eingelöst.',
    branderror: props.customBrandError
      ? props.customBrandError
      : 'Der eingegebene Code ist nicht für diese Produkte gültig.',
  },
};

const couponInput = ref<string>('');

const emits = defineEmits(['submit-coupon', 'erase-coupon']);

const submitCoupon = () => {
  emits('submit-coupon', couponInput.value);
};

const eraseCoupon = () => {
  couponInput.value = '';
  emits('erase-coupon');
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.coupon-teaser {
  display: flex;
  flex-direction: column-reverse;
  padding: $grid-base;
  margin-bottom: 5 * $grid-base;

  @include mq($mq-sm) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: $grid-base;
  }

  @include mq($mq-md) {
    padding: 3 * $grid-base;
    gap: 4 * $grid-base;
  }

  @include mq($mq-lg) {
    padding: 5 * $grid-base;
  }

  &__copy {
    @include mq($mq-sm) {
      flex-basis: 60%;
    }
  }

  &__heading {
    @include font-size(36px, 36px);
    margin: $grid-base 0;

    @include mq($mq-sm) {
      margin-bottom: 2 * $grid-base;
    }

    @include mq($mq-md) {
      @include font-size(48px, 48px);
    }

    @include mq($mq-lg) {
      white-space: nowrap;
    }
  }

  &__paragraph {
    @include mq($mq-sm) {
      margin-bottom: 5 * $grid-base;
    }
    @include mq($mq-md) {
      @include font-size(24px, 29px);
    }
  }

  &__hero-image {
    width: 60%;
    height: auto;
    align-self: flex-end;
    max-width: 20 * $grid-base;

    @include mq($mq-sm) {
      align-self: center;
      max-width: 370px;
    }

    @include mq($mq-md) {
      max-width: none;
    }
  }

  &__form {
    width: 100%;
    height: 4 * $grid-base;
    display: flex;
    gap: 0.5 * $grid-base;

    @include mq($mq-sm) {
      gap: $grid-base;
    }

    @include mq($mq-md) {
      gap: 2 * $grid-base;
    }

    :deep(.input-wrapper__input),
    &-submit {
      height: 100%;
    }

    :deep(.input-wrapper__input) {
      @include mq(false, $mq-md - 1) {
        @include font-size(15px, 24px);
      }
    }

    &-text {
      flex-grow: 1;
    }

    &-submit {
      &--mobile {
        @include mq($mq-md) {
          display: none;
        }
      }

      &--desktop {
        @include mq(false, $mq-md - 1) {
          display: none;
        }
      }
    }

    &-post-submit {
      display: flex;
      align-items: flex-end;
      gap: 0.5 * $grid-base;

      @include mq($mq-md) {
        gap: $grid-base;
      }

      p {
        margin-bottom: 0;
      }
    }

    &-active-coupon {
      flex-grow: 0.5;
    }

    &-trash {
      &-icon {
        width: 20px;
        height: auto;
      }
    }
  }

  &__warning-message {
    margin-top: $grid-base;
    color: color(red, 2000);
  }

  &--mini {
    gap: $grid-base;
    max-width: $mq-xl;
    margin: 0 auto;

    @include mq($mq-sm) {
      padding: 0 $grid-base;
    }

    .coupon-teaser {
      &__hero-image {
        margin-bottom: 2 * $grid-base;
        max-width: 350px;
      }

      &__paragraph {
        margin-bottom: $grid-base;
      }

      &__form {
        max-width: 500px;
      }
    }
  }
}
</style>
